import { html, nothing } from "lit-html";
import type { View, App } from "../app.js";
import { api } from "../api.js";
import type { Scorecards, Trend, QueryResult } from "../types.js";
import { targetKpis, pacePill, meter, sectionHead, delta, resultTable } from "../components.js";
import { money, moneyShort, pct } from "../format.js";
import { divisionIcon } from "../icons.js";
import { hbars } from "../charts.js";
import { trendChart } from "./overview.js";

interface Data { key: string; sc: Scorecards; trend: Trend; subs: QueryResult; customers: QueryResult; all: Scorecards }

export const divisions: View<Data> = {
  title: (app) => {
    const k = app.param ?? app.me.divisions[0]?.key;
    return app.me.divisions.find((d) => d.key === k)?.name ?? "Divisions";
  },
  filters: ["year", "manager", "rep"],
  async load(app, param) {
    const key = param && app.me.divisions.some((d) => d.key === param) ? param : app.me.divisions[0]?.key;
    if (!key) throw new Error("You don't have access to any divisions.");
    const { from, to } = app.yearRange();
    const base = { ...app.queryFilters(), division: [key] };
    const [sc, trend, subs, customers, all] = await Promise.all([
      api.scorecards(app.q({ division: key })),
      api.trend(app.q({ division: key })),
      api.query({ dims: ["subdivision"], metrics: ["revenue", "gp_pct"], filters: base, range: "custom", from, to, compare: "prior_year", limit: 50 }),
      api.query({ dims: ["customer"], metrics: ["revenue", "invoices"], filters: base, range: "custom", from, to, compare: "prior_year", limit: 15 }),
      api.scorecards(app.q({ division: null })),
    ]);
    return { key, sc, trend, subs, customers, all };
  },
  render(app: App, d) {
    const div = app.me.divisions.find((x) => x.key === d.key)!;
    const t = d.sc.byDivision[d.key] ?? d.sc.total;
    const subRows = div.subs.map((s) => {
      const row = d.subs.rows.find((r) => r.key.subdivision === `${d.key}/${s.key}`);
      return { name: s.name, ytd: row?.values.revenue ?? 0, prior: row?.prior?.revenue ?? 0, gp: row?.values.gp_pct ?? null };
    });
    const withSales = d.sc.rows.filter((r) => r.byDivision[d.key] && (r.byDivision[d.key]!.ytd !== 0 || r.byDivision[d.key]!.priorFull !== 0));
    // People only (no House, GSA, Web accounts), unless the division is run entirely through one (Apparatus).
    const people = withSales.filter((r) => !r.shared);
    const reps = people.length ? people : withSales;
    return html`
      <div class="tabs" role="tablist">
        ${app.me.divisions.map((x) => {
          const tt = d.all.byDivision[x.key];
          return html`<a class="tab" role="tab" aria-selected=${x.key === d.key} href=${app.href("divisions", x.key)}>${x.name}${tt ? html` <span class="muted small">${moneyShort(tt.ytd)}</span>` : nothing}</a>`;
        })}
      </div>

      <div class="grid g-kpi">${targetKpis(t, d.sc.growth)}</div>

      <div class="grid g-3 mt">
        ${subRows.map((s) => html`<div class="glass div-card" style="cursor:default">
          <div class="div-top"><div class="div-icon">${divisionIcon[d.key] ?? nothing}</div><div class="div-name">${s.name}</div></div>
          <div class="div-val">${money(s.ytd)}</div>
          <div class="kpi-foot">${delta(s.ytd, s.prior)}<span>vs ${moneyShort(s.prior)} last year</span>${s.gp !== null ? html`<span>· GP ${pct(s.gp)}</span>` : nothing}</div>
        </div>`)}
      </div>

      <div class="grid g-2 mt">
        <section class="glass card">
          ${sectionHead("Monthly revenue", `${div.name}, ${d.trend.year} vs ${d.trend.year - 1}`)}
          ${trendChart(d.trend, d.sc.growth, 250)}
        </section>
        <section class="glass card">
          ${sectionHead("Top customers", "This year · tick = same period last year")}
          ${hbars(d.customers.rows.slice(0, 10).map((r) => ({ label: r.label.customer ?? "", value: r.values.revenue ?? 0, ghost: r.prior?.revenue ?? null })), moneyShort, { ghostName: "Last year" })}
        </section>
      </div>

      <section class="glass card mt">
        ${sectionHead(`${div.name} by rep`, `Target = ${Math.round((1 + d.sc.growth) * 100)}% of each rep's ${d.trend.year - 1} ${div.name.toLowerCase()} revenue`)}
        ${reps.length ? html`<div class="table-wrap"><table class="t">
          <thead><tr><th>Rep</th><th class="r">YTD</th><th class="r">Last yr same period</th><th class="r">Change</th><th class="r">Full-yr target</th><th>Progress</th><th class="r">Projected</th><th class="r">Pace</th></tr></thead>
          <tbody>${reps.map((r) => {
            const x = r.byDivision[d.key]!;
            return html`<tr class="click" @click=${() => app.go("reps", String(r.repId))}>
              <td class="name">${r.repName}</td><td class="r">${money(x.ytd)}</td><td class="r muted">${money(x.priorYtd)}</td>
              <td class="r">${delta(x.ytd, x.priorYtd)}</td><td class="r">${money(x.target)}</td><td class="meter-cell">${meter(x)}</td>
              <td class="r">${money(x.projected)}</td><td class="r">${pacePill(x.pace, true)}</td></tr>`;
          })}</tbody></table></div>` : html`<div class="empty-state">No ${div.name.toLowerCase()} sales for these reps.</div>`}
      </section>

      <section class="glass card mt">
        ${sectionHead("Customers", "This year vs same period last year")}
        ${resultTable(d.customers, { maxHeight: 420, onRow: (row) => row.key.customer && app.go("customer", String(row.key.customer)) })}
      </section>`;
  },
};
